import { ReactNode } from 'react';
import { FieldErrors, FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent, CardFooter } from '@/components/ui-kit/card';
import { useToast } from '@/hooks/use-toast';
import { useReportUnreportedError } from '@/state/query-client/hooks';
import { RequiredFieldsProvider } from './required-fields';

export interface TabFormProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  /** Resolves `false` when nothing was saved after all (e.g. a confirmation was declined). */
  onSave: (values: T) => Promise<void | boolean>;
  isSaving: boolean;
  /** Translation key of the submit button. */
  submitLabel?: string;
  /** Lets an unchanged form be saved, e.g. a pre-filled profile the person must still confirm as complete. */
  allowPristineSave?: boolean;
  /** Fields whose labels get a `*` (see `requiredFieldsOf`); none when left out. */
  requiredFields?: ReadonlySet<string>;
  children: ReactNode;
}

const NO_REQUIRED_FIELDS: ReadonlySet<string> = new Set();

/**
 * One edit tab: its own form and its own save, since every tab is stored in its own record. Nothing is
 * saved until something changed; after a save the form treats the saved values as its new baseline.
 */
export const TabForm = <T extends FieldValues>({
  form,
  onSave,
  isSaving,
  submitLabel = 'SAVE_CHANGES',
  allowPristineSave = false,
  requiredFields = NO_REQUIRED_FIELDS,
  children,
}: TabFormProps<T>) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const reportError = useReportUnreportedError();

  const handleSubmit = async (values: T) => {
    try {
      const saved = await onSave(values);
      if (saved !== false) form.reset(form.getValues());
    } catch (error) {
      reportError(error);
    }
  };

  // The fields show their own messages, but one can sit out of view (or have none, like the photo),
  // so a failed check is also announced; the first invalid field is focused by react-hook-form.
  const handleInvalid = (errors: FieldErrors<T>) => {
    console.warn('Form validation failed', errors);
    const firstMessage = Object.values(errors).find(
      (error) => typeof error?.message === 'string'
    )?.message;
    toast({
      variant: 'destructive',
      title: t('PLEASE_FILL_ALL_REQUIRED_FIELDS'),
      description: typeof firstMessage === 'string' ? firstMessage : undefined,
    });
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !(event.target instanceof HTMLTextAreaElement))
            event.preventDefault();
        }}
      >
        <Card>
          <CardContent className="pt-6">
            <RequiredFieldsProvider value={requiredFields}>{children}</RequiredFieldsProvider>
          </CardContent>
          <CardFooter className="justify-end gap-3 border-t bg-muted/30 pt-6">
            <Button
              type="button"
              variant="outline"
              className="flex-1 sm:min-w-28 sm:flex-none"
              onClick={() => form.reset()}
              disabled={!form.formState.isDirty || isSaving}
            >
              {t('CANCEL')}
            </Button>
            <Button
              type="submit"
              className="flex-1 sm:min-w-28 sm:flex-none"
              loading={isSaving}
              disabled={(!form.formState.isDirty && !allowPristineSave) || isSaving}
            >
              {t(submitLabel)}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </FormProvider>
  );
};
